import React, { FC } from 'react';
import { Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';

import { Location } from '~/types';

import { useDeepEffect, useMCC, usePath, usePermissions } from '~/hooks';
import { locations } from '~/routes';
import buildAddress from '~/utils/buildAddress';
import { statusCell } from '~/utils';
import { usePageShow } from '~/hooks/usePageQuery';
import { LocationIcon } from '~/img';
import { Avatar } from '~/components';

interface Props {
  data: Location[] | undefined;
  loading: boolean;
  total: any;
}

const LocationsTable: FC<Props> = (props) => {
  const { data, loading, total } = props;
  const { pagination, changePath } = usePath();
  const { showOpen } = usePageShow(locations);
  const { t } = useTranslation('locations');
  const { getMCC } = useMCC();
  const hasAccess = usePermissions('business.location.view');

  useDeepEffect(() => {
    changePath({ page: 1, perPage: 10 });
  }, []);

  const columns: ColumnsType<Location> = [
    statusCell(),
    {
      title: '',
      dataIndex: 'icon',
      render: (_, record) => {
        return <Avatar icon={<LocationIcon />} file={record?.branding?.icon as any} />;
      },
      className: 'AvatarCell',
    },
    {
      title: t('name'),
      dataIndex: 'name',
    },
    {
      title: t('address'),
      dataIndex: 'address',
      render: (_, record) => {
        const { latitude, longitude } = record.address?.geolocation || {};
        return (
          <div>
            {latitude && longitude && (
              <Tooltip title={`${latitude}, ${longitude}`}>
                <LocationIcon className="text-gray" />
              </Tooltip>
            )}{' '}
            {buildAddress(record.address)}
          </div>
        );
      },
    },
    {
      title: t('MCC'),
      dataIndex: 'merchantCategoryCode',
      render: (_, record) => {
        return <div>{getMCC(record.merchantCategoryCode)}</div>;
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      rowKey={(record) => record?.id as string}
      pagination={{ ...pagination, total }}
      rowClassName="cursor-pointer"
      onRow={(record) => ({
        onClick: () => hasAccess && showOpen(record.id as string),
      })}
    />
  );
};

export default LocationsTable;
